import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchRecipes } from '../store/recipesSlice';
import { Link } from 'react-router-dom';

function Search(): React.ReactElement {
  const dispatch = useAppDispatch();
  const { items: recipes, status } = useAppSelector((state) => state.recipes);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRecipes, setFilteredRecipes] = useState(recipes);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchRecipes());
  }, [dispatch, status]);

  useEffect(() => {
    const filtered = recipes.filter(recipe =>
      recipe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.ingredients.some(i => i.food_name?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredRecipes(filtered);
  }, [searchTerm, recipes]);

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Search Recipes</h2>

      <div className="relative mb-8">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, description, or ingredient..."
          className="w-full px-4 py-3 pl-10 bg-army-900 border border-army-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors"
        />
        <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-army-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-army-700 hover:text-green-400 transition-colors">
            ✕
          </button>
        )}
      </div>

      {status === 'loading' ? (
        <div className="text-center py-12 text-army-600">Loading recipes...</div>
      ) : (
        <div className="grid gap-3">
          {filteredRecipes.map((recipe) => (
            <div key={recipe.id} className="bg-navy-900 border border-navy-800 rounded-xl p-5 hover:border-navy-700 transition-colors group">
              <h3 className="text-lg font-semibold text-white mb-1">{recipe.name}</h3>
              {recipe.description && (
                <p className="text-gray-400 text-sm mb-3">{recipe.description}</p>
              )}
              {recipe.ingredients.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {recipe.ingredients.map((ingredient) => (
                    <span key={ingredient.id} className="inline-block px-2 py-0.5 bg-army-900 text-gray-500 text-xs rounded border border-army-800">
                      {ingredient.measurement && `${ingredient.measurement} `}{ingredient.food_name}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-4">
                <Link to={`/recipes/${recipe.id}`} className="text-xs font-semibold text-neon hover:brightness-90 transition-all">
                  View Recipe →
                </Link>
                <Link to={`/recipes/${recipe.id}/edit`} className="text-xs text-gray-500 hover:text-white transition-colors">
                  Edit
                </Link>
              </div>
            </div>
          ))}
          {filteredRecipes.length === 0 && (
            <div className="text-center py-12 text-army-700">
              {searchTerm ? <>No recipes match <span className="text-army-600">"{searchTerm}"</span></> : 'No recipes yet.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Search;
